import * as React from 'react';
import { LuChevronDown } from 'react-icons/lu';

import { ClockIcon } from './Icons';
import { Button } from './ui/button';
import { Calendar } from './ui/calendar';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

interface DateTimePickerProps {
    value?: string; // ISO string
    onChange?: (value: string) => void;
    label?: string;
    placeholder?: string;
    className?: string;
}

export function DateTimePicker({ value, onChange, label, placeholder, className }: DateTimePickerProps) {
    const [dateOpen, setDateOpen] = React.useState(false);
    const [timeOpen, setTimeOpen] = React.useState(false);
    const [manualTimeInput, setManualTimeInput] = React.useState('');
    const [inputError, setInputError] = React.useState<string | null>(null);
    const inputRef = React.useRef<HTMLInputElement>(null);

    // Parse the ISO string value into date and time
    const dateValue = value ? new Date(value) : undefined;
    const timeString = dateValue
        ? `${String(dateValue.getHours()).padStart(2, '0')}:${String(dateValue.getMinutes()).padStart(2, '0')}`
        : '10:30';

    // Update manual time input when value changes
    React.useEffect(() => {
        if (dateValue) {
            const hours = dateValue.getHours();
            const minutes = dateValue.getMinutes();
            const formattedHour = hours % 12 === 0 ? 12 : hours % 12;
            const period = hours < 12 ? 'am' : 'pm';
            setManualTimeInput(`${formattedHour}:${String(minutes).padStart(2, '0')}${period}`);
            setInputError(null);
        }
    }, [value]);

    // Generate time options in 15-minute intervals
    const generateTimeOptions = () => {
        const options = [];
        for (let hour = 0; hour < 24; hour++) {
            for (let minute = 0; minute < 60; minute += 15) {
                const formattedHour = hour % 12 === 0 ? 12 : hour % 12;
                const period = hour < 12 ? 'am' : 'pm';
                const formattedTime = `${formattedHour}:${String(minute).padStart(2, '0')}${period}`;
                options.push({
                    label: formattedTime,
                    value: `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`,
                });
            }
        }
        return options;
    };

    const timeOptions = generateTimeOptions();

    // Handle date selection
    const handleDateSelect = (date: Date | undefined) => {
        if (!date) return;

        // Preserve the time from the current value or use default
        const currentDate = value ? new Date(value) : new Date();
        date.setHours(currentDate.getHours(), currentDate.getMinutes(), currentDate.getSeconds());

        if (onChange) {
            onChange(date.toISOString());
        }
        setDateOpen(false);
    };

    // Handle time selection from dropdown
    const handleTimeSelect = (timeStr: string) => {
        if (!onChange) return;

        // Use current date or create new one
        const date = dateValue || new Date();
        const [hours, minutes] = timeStr.split(':').map(Number);

        date.setHours(hours || 0, minutes || 0, 0);
        onChange(date.toISOString());
        setTimeOpen(false);
        setInputError(null);
    };

    // Parse manually entered time
    const parseManualTime = (input: string): { hours: number; minutes: number } | null => {
        // Handle formats like "3:30pm", "15:30", "3pm", "3:30 pm", etc.
        const timeRegex = /^(\d{1,2})(?::(\d{2}))?(?:\s*)?(am|pm)?$/i;
        const match = input.trim().match(timeRegex);

        if (!match) return null;

        let hours = parseInt(match[1], 10);
        const minutes = match[2] ? parseInt(match[2], 10) : 0;
        const period = match[3]?.toLowerCase();

        // Handle 12-hour format with am/pm
        if (period === 'pm' && hours < 12) {
            hours += 12;
        } else if (period === 'am' && hours === 12) {
            hours = 0;
        }

        // Validate hours and minutes
        if (hours >= 24 || minutes >= 60) return null;

        return { hours, minutes };
    };

    // Handle manual time input
    const handleManualTimeSubmit = () => {
        const parsedTime = parseManualTime(manualTimeInput);

        if (!parsedTime) {
            setInputError('Please use format: 3:30pm or 15:30');
            if (inputRef.current) {
                inputRef.current.focus();
            }
            return;
        }

        setInputError(null);
        const { hours, minutes } = parsedTime;
        const date = dateValue || new Date();
        date.setHours(hours, minutes, 0);

        if (onChange) {
            onChange(date.toISOString());
        }
        setTimeOpen(false);
    };

    // Format time for display
    const formatDisplayTime = (timeStr: string) => {
        const [hours, minutes] = timeStr.split(':').map(Number);
        const formattedHour = hours % 12 === 0 ? 12 : hours % 12;
        const period = hours < 12 ? 'am' : 'pm';
        return `${formattedHour}:${String(minutes).padStart(2, '0')}${period}`;
    };

    return (
        <div className={`space-y-2 ${className}`}>
            {label && <Label className="px-1">{label}</Label>}
            <div className="flex gap-2">
                <div className="flex-1">
                    <Popover open={dateOpen} onOpenChange={setDateOpen}>
                        <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full justify-between font-normal">
                                {dateValue ? dateValue.toLocaleDateString() : placeholder || 'Select date'}
                                <LuChevronDown className="ml-2 h-4 w-4" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto overflow-hidden p-0" align="start">
                            <Calendar
                                mode="single"
                                selected={dateValue}
                                captionLayout="dropdown"
                                onSelect={handleDateSelect}
                            />
                        </PopoverContent>
                    </Popover>
                </div>
                <div className="w-[120px]">
                    <Popover
                        open={timeOpen}
                        onOpenChange={(open) => {
                            setTimeOpen(open);
                            if (!open) {
                                setInputError(null);
                            }
                        }}
                    >
                        <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full justify-between font-normal">
                                {timeString ? formatDisplayTime(timeString) : 'Time'}
                                <ClockIcon className="ml-2 h-4 w-4" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[220px] p-0" align="start">
                            <div className="border-b p-2">
                                <div className="flex flex-col gap-2">
                                    <div className="flex items-center">
                                        <Input
                                            ref={inputRef}
                                            value={manualTimeInput}
                                            onChange={(e) => {
                                                setManualTimeInput(e.target.value);
                                                if (inputError) setInputError(null);
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    handleManualTimeSubmit();
                                                }
                                            }}
                                            className="flex-1"
                                            placeholder="e.g. 3:30pm or 15:30"
                                        />
                                        <Button size="sm" onClick={handleManualTimeSubmit} className="ml-1">
                                            OK
                                        </Button>
                                    </div>
                                    {inputError && <div className="px-1 text-xs text-red-500">{inputError}</div>}
                                </div>
                            </div>
                            <div className="h-72 overflow-y-auto">
                                <div className="grid">
                                    {timeOptions.map((option) => (
                                        <Button
                                            key={option.value}
                                            variant="ghost"
                                            className={`justify-start rounded-none text-left font-normal ${
                                                timeString === option.value ? 'bg-accent text-accent-foreground' : ''
                                            }`}
                                            onClick={() => handleTimeSelect(option.value)}
                                        >
                                            {option.label}
                                        </Button>
                                    ))}
                                </div>
                            </div>
                        </PopoverContent>
                    </Popover>
                </div>
            </div>
        </div>
    );
}
